/**
 * De beschrijving van een woning: alles wat een gast leest.
 *
 * Dit scherm ontbrak, en dat was geen kleinigheid: het datamodel kende tien
 * tekstvelden per taal en het instellingenscherm liet er twee zien. Een verhuurder
 * kon zijn appartement dus niet beschrijven — geen kop, geen samenvatting, geen
 * omschrijving, geen locatietekst — en zonder die teksten weigert de
 * publicatiecontrole terecht om de woning live te zetten.
 *
 * # De taal staat los van de taal van het dashboard
 *
 * Het instellingenscherm bewerkte de content in de taal waarin het dashboard
 * toevallig stond. Wie zijn dashboard op Nederlands had, kon de Engelse tekst niet
 * invullen zonder de hele omgeving om te zetten — en beide talen moeten gevuld zijn
 * (regel 6). De taalkiezer hier hoort dus bij de content en niet bij de interface.
 *
 * # Wat er per veld op het spel staat, staat erbij
 *
 * Een samenvatting die te lang is, wordt door een kanaal afgekapt; een kop die
 * ontbreekt, laat de woning niet publiceren. Dat soort gevolgen hoort naast het veld
 * te staan en niet in een handleiding.
 */
import { useCallback, useEffect, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import type { Locale } from '@siteshowroom/shared'

import { useLocale, useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  fetchResidence,
  listAmenityCatalogue,
  residenceErrorMessageKey,
  saveAmenities,
  saveContent,
  updateResidence,
} from '../lib/residence-client'
import type {
  Amenity,
  ContentPatch,
  ResidenceContent,
  ResidenceFailure,
  ResidenceResult,
} from '../lib/residence-client'

/** De velden, gegroepeerd zoals een verhuurder ze invult. */
const GROUPS = [
  {
    key: 'presentation',
    fields: ['headline', 'summary', 'description', 'locationDescription'],
  },
  {
    key: 'practical',
    fields: [
      'houseRules',
      'arrivalInstructions',
      'departureInstructions',
      // Ook op het instellingenscherm te bewerken, naast de betaalmodus waar hij bij
      // hoort. Twee ingangen naar hetzelfde veld is hier geen probleem meer: een
      // deelopslag laat de rest staan (§17.36).
      'paymentInstructions',
    ],
  },
  { key: 'findability', fields: ['metaTitle', 'metaDescription'] },
] as const

/** Welke velden één regel zijn en welke een blok tekst. */
const SINGLE_LINE: readonly string[] = ['headline', 'metaTitle']

/** Bovengrenzen, gelijk aan die van de API. */
const LIMITS: Readonly<Record<string, number>> = {
  headline: 200,
  summary: 500,
  description: 10_000,
  locationDescription: 10_000,
  houseRules: 10_000,
  arrivalInstructions: 10_000,
  departureInstructions: 10_000,
  paymentInstructions: 10_000,
  metaTitle: 200,
  metaDescription: 500,
}

const EMPTY: Record<string, string> = {
  headline: '',
  summary: '',
  description: '',
  locationDescription: '',
  houseRules: '',
  arrivalInstructions: '',
  departureInstructions: '',
  paymentInstructions: '',
  metaTitle: '',
  metaDescription: '',
}

function FailureNotice({ error }: { readonly error: ResidenceFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(residenceErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

export function ContentPage(): ReactElement {
  const t = useTranslate()
  const { locales } = useLocale()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  // De taal van de CONTENT, los van die van het scherm. Zie de kop.
  const [contentLocale, setContentLocale] = useState<Locale>(locales[0] ?? 'en')
  const [fields, setFields] = useState<Record<string, string>>(EMPTY)
  const [filled, setFilled] = useState<Readonly<Record<string, boolean>>>({})
  // De naam van de woning staat los van de talen: hij hoort bij de woning en niet
  // bij de tekst. Hij vult `{{residence_name}}` in elk bericht.
  const [name, setName] = useState('')
  const [catalogue, setCatalogue] = useState<readonly Amenity[]>([])
  const [chosen, setChosen] = useState<readonly string[]>([])
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<ResidenceFailure | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([listResidences(), listAmenityCatalogue()]).then(([list, amenities]) => {
      if (cancelled) {
        return
      }
      if (list.ok) {
        setResidences(list.data)
        const first = list.data[0]
        setResidenceId(first === undefined ? null : first.id)
      }
      if (amenities.ok) {
        setCatalogue(amenities.data)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    const result = await fetchResidence(residenceId)
    if (!result.ok) {
      setError(result.error)
      return
    }
    const content = result.data.content.find((entry) => entry.locale === contentLocale)
    setFields(content === undefined ? EMPTY : toFields(content))
    setName(result.data.name)
    setChosen(result.data.amenityKeys)
    // Welke talen al iets hebben: dat bepaalt of de woning te publiceren is, en het
    // is de vraag die een verhuurder met twee talen als eerste stelt.
    setFilled(
      Object.fromEntries(
        locales.map((locale) => [
          locale,
          (result.data.content.find((entry) => entry.locale === locale)?.headline ?? '') !== '',
        ]),
      ),
    )
  }, [residenceId, contentLocale, locales])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(null)
    setSaved(false)
    void load()
  }, [load])

  const run = useCallback(
    async (work: () => Promise<ResidenceResult<unknown>>): Promise<void> => {
      setBusy(true)
      setError(null)
      const result = await work()
      if (result.ok) {
        setSaved(true)
      } else {
        setError(result.error)
      }
      await load()
      setBusy(false)
    },
    [load],
  )

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (residenceId === null) {
      return
    }
    const patch: ContentPatch = {}
    for (const key of Object.keys(EMPTY)) {
      // Een leeg veld wordt óók opgeslagen: leegmaken is een geldige handeling, en
      // lege velden overslaan zou betekenen dat iets wat je weghaalt terugkomt.
      ;(patch as Record<string, string>)[key] = fields[key] ?? ''
    }
    void run(() => saveContent(residenceId, contentLocale, patch))
  }

  return (
    <section className="page" aria-labelledby="content-heading">
      <div className="page__header">
        <h1 className="page__title" id="content-heading">
          {t('dashboard.content.title')}
        </h1>
      </div>

      <p className="prose">{t('dashboard.content.intro')}</p>

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.media.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {residenceId !== null && (
        <form
          className="name-form"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => updateResidence(residenceId, { name: name.trim() }))
          }}
        >
          <label className="field">
            <span className="field__label">{t('dashboard.content.field.name')}</span>
            <input
              className="field__input"
              disabled={busy}
              maxLength={200}
              onChange={(event) => {
                setName(event.target.value)
              }}
              type="text"
              value={name}
            />
            <span className="field__hint">{t('dashboard.content.hint.name')}</span>
          </label>
          <button className="button" disabled={busy} type="submit">
            {t('dashboard.content.saveName')}
          </button>
        </form>
      )}

      {/* De taal van de tekst, niet die van het scherm. Beide talen moeten gevuld
          zijn voordat er gepubliceerd kan worden (regel 6). */}
      <div className="locale-tabs" role="tablist">
        {locales.map((locale) => (
          <button
            aria-selected={locale === contentLocale}
            className="locale-tab"
            data-filled={filled[locale] === true ? 'true' : undefined}
            key={locale}
            onClick={() => {
              setContentLocale(locale)
            }}
            role="tab"
            type="button"
          >
            {t(`common.language.${locale}`)}
            {filled[locale] !== true && (
              <span className="locale-tab__todo">{t('dashboard.content.locale.empty')}</span>
            )}
          </button>
        ))}
      </div>

      {error !== null && <FailureNotice error={error} />}
      {saved && (
        <p className="page__status" role="status">
          {t('dashboard.content.saved')}
        </p>
      )}

      {residenceId !== null && (
        <>
          <form className="content-form" onSubmit={submit}>
            {GROUPS.map((group) => (
              <fieldset className="content-group" key={group.key}>
                <legend className="page__sectionTitle">
                  {t(`dashboard.content.group.${group.key}`)}
                </legend>
                <p className="prose">{t(`dashboard.content.group.${group.key}.intro`)}</p>

                {group.fields.map((field) => (
                  <label className="field" key={field}>
                    <span className="field__label">{t(`dashboard.content.field.${field}`)}</span>
                    {SINGLE_LINE.includes(field) ? (
                      <input
                        className="field__input"
                        disabled={busy}
                        maxLength={LIMITS[field]}
                        onChange={(event) => {
                          setFields((current) => ({ ...current, [field]: event.target.value }))
                        }}
                        type="text"
                        value={fields[field] ?? ''}
                      />
                    ) : (
                      <textarea
                        className="field__input"
                        disabled={busy}
                        maxLength={LIMITS[field]}
                        onChange={(event) => {
                          setFields((current) => ({ ...current, [field]: event.target.value }))
                        }}
                        rows={field === 'summary' || field === 'metaDescription' ? 3 : 8}
                        value={fields[field] ?? ''}
                      />
                    )}
                    <span className="field__hint">
                      {t(`dashboard.content.hint.${field}`)}{' '}
                      {t('dashboard.content.counter', {
                        used: (fields[field] ?? '').length,
                        max: LIMITS[field] ?? 0,
                      })}
                    </span>
                  </label>
                ))}
              </fieldset>
            ))}

            <button className="button button--primary" disabled={busy} type="submit">
              {t('dashboard.content.save', { language: t(`common.language.${contentLocale}`) })}
            </button>
          </form>

          <h2 className="page__sectionTitle">{t('dashboard.content.amenities.title')}</h2>
          <p className="prose">{t('dashboard.content.amenities.intro')}</p>

          {catalogue.length === 0 ? (
            <p className="page__status" role="status">
              {t('dashboard.content.amenities.empty')}
            </p>
          ) : (
            <>
              {/* Gegroepeerd zoals Airbnb ze groepeert (§17.34). Een lijst van ruim
                  honderd vinkjes zonder koppen is niet te overzien, en de meeste
                  verhuurders kennen deze indeling al van hun eigen advertentie. */}
              {groupByCategory(catalogue).map(([category, items]) => (
                <fieldset className="amenity-group" key={category}>
                  <legend className="field__label">{t(`amenities.categories.${category}`)}</legend>
                  <ul className="amenity-list">
                    {items.map((amenity) => (
                      <li className="amenity" key={amenity.key}>
                        <label>
                          <input
                            checked={chosen.includes(amenity.key)}
                            disabled={busy}
                            onChange={(event) => {
                              setChosen((current) =>
                                event.target.checked
                                  ? [...current, amenity.key]
                                  : current.filter((key) => key !== amenity.key),
                              )
                            }}
                            type="checkbox"
                          />
                          {/* De naam komt uit de vertaalbestanden en niet uit de
                              database: de sleutel is een vaste woordenlijst (regel 6). */}
                          <span>{t(`amenities.items.${amenity.key}`)}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              ))}
              <button
                className="button button--primary"
                disabled={busy}
                onClick={() => {
                  void run(() => saveAmenities(residenceId, chosen))
                }}
                type="button"
              >
                {t('dashboard.content.amenities.save', { count: chosen.length })}
              </button>
            </>
          )}
        </>
      )}
    </section>
  )
}

function toFields(content: ResidenceContent): Record<string, string> {
  return {
    headline: content.headline,
    summary: content.summary,
    description: content.description,
    locationDescription: content.locationDescription,
    houseRules: content.houseRules,
    arrivalInstructions: content.arrivalInstructions,
    departureInstructions: content.departureInstructions,
    paymentInstructions: content.paymentInstructions,
    metaTitle: content.metaTitle,
    metaDescription: content.metaDescription,
  }
}

/**
 * De voorzieningen per categorie, in de volgorde waarin de catalogus ze levert.
 *
 * `sort_order` bepaalt de volgorde binnen én tussen de groepen: de eerste keer dat
 * een categorie voorkomt, bepaalt waar die groep staat. Zo staat de volgorde op één
 * plek — in de database — en niet ook nog eens hier.
 */
function groupByCategory(catalogue: readonly Amenity[]): [string, Amenity[]][] {
  const groups = new Map<string, Amenity[]>()
  for (const amenity of catalogue) {
    const existing = groups.get(amenity.category)
    if (existing === undefined) {
      groups.set(amenity.category, [amenity])
    } else {
      existing.push(amenity)
    }
  }
  return [...groups.entries()]
}
